import { useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { CcpSectionKey } from "./ccp-constants"
import type {
  FieldCitation,
  FieldUpdatePayload,
  PlanGenerateInput,
  SegmentReport,
} from "../../types"
import type { CompanyDirectoryEntry } from "../../utils/segment-connections"
import type { PlanSectionView, PlanViewMode } from "../plan/plan-utils"

import { Skeleton } from "@/components/ui/skeleton"

import { CCP_SECTIONS } from "./ccp-constants"
import { CcpSectionBody } from "./ccp-section-body"
import { useCcp } from "../../hooks/use-ccp"
import { usePlanLibraryDocs } from "../../hooks/use-plan-library-docs"
import { GenerateModal } from "../plan/generate-modal"
import { PlanEmptyState, ThinEvidenceBanner } from "../plan/plan-callouts"
import { PlanCanvasView } from "../plan/plan-canvas-view"
import { PlanDocumentView } from "../plan/plan-document-view"
import { PlanShell } from "../plan/plan-shell"
import {
  ccpLabelledFields,
  dataGapLabels,
  formatPlanDate,
  PLAN_VIEW_MODE_STORAGE_KEY,
  planIsGenerated,
  planIsMostlyNs,
} from "../plan/plan-utils"
import { UpdateReviewModal } from "../plan/update-review-modal"
import { RelatedCompaniesRow } from "../related-companies-row"
import { ConfidenceBadge } from "../segment-detail-primitives"

function readInitialMode(): PlanViewMode {
  const stored = sessionStorage.getItem(PLAN_VIEW_MODE_STORAGE_KEY)
  return stored === "canvas" ? "canvas" : "document"
}

const SECTION_VIEWS: PlanSectionView[] = CCP_SECTIONS.map((def) => ({
  no: def.no,
  title: def.title,
  fieldKey: def.fieldKey,
  updatable: true,
  colSpan: def.canvas.colSpan,
  rowSpan: def.canvas.rowSpan,
}))

const updateTestId = (fieldKey: string) => `sa-plan-update-${fieldKey}`

export function CcpPlanView({
  segmentId,
  companyId,
  companyName,
  segmentName,
  segmentReport = null,
  canEdit,
  autoDraft = false,
  directory,
  onBack,
  onOpenCompany,
}: {
  segmentId: string
  companyId: string
  companyName: string
  segmentName: string
  /** Segment report evidence — feeds GenerateModal source selection when present. */
  segmentReport?: SegmentReport | null
  canEdit: boolean
  /** Prospects: auto-start generate on first open when no CCP exists yet. */
  autoDraft?: boolean
  /** Portfolio company directory — feeds the "Related" lateral-nav row. */
  directory?: CompanyDirectoryEntry[]
  onBack: () => void
  /** Drill into a related company (omit to hide the Related row). */
  onOpenCompany?: (name: string, segmentId: string) => void
}) {
  const { t } = useTranslation()
  const { planQuery, generateMutation, requestUpdate, patchField } = useCcp(segmentId, companyId)
  const libraryDocs = usePlanLibraryDocs(companyId, segmentReport, canEdit)
  const [mode, setMode] = useState<PlanViewMode>(readInitialMode)
  const [generateOpen, setGenerateOpen] = useState(false)
  const [review, setReview] = useState<{ fieldKey: string; title: string } | null>(null)
  const [justGenerated, setJustGenerated] = useState(false)
  const autoDraftStarted = useRef(false)

  const plan = planQuery.data ?? null
  const generated = plan ? planIsGenerated(plan) : false

  const generatePlan = generateMutation.mutate
  const isGenerating = generateMutation.isPending

  // Prospects: when opening a company with no CCP yet, kick off generate once + fresh banner.
  useEffect(() => {
    if (!autoDraft || !canEdit) return
    if (planQuery.isPending || planQuery.isFetching) return
    if (generated || isGenerating) return
    if (autoDraftStarted.current) return
    autoDraftStarted.current = true
    generatePlan(
      {},
      {
        onSuccess: () => setJustGenerated(true),
        onError: () => toast.error(t("segmentAnalysis.plan.generateFailed")),
      }
    )
  }, [
    autoDraft,
    canEdit,
    generatePlan,
    generated,
    isGenerating,
    planQuery.isFetching,
    planQuery.isPending,
    t,
  ])

  function changeMode(next: PlanViewMode) {
    setMode(next)
    sessionStorage.setItem(PLAN_VIEW_MODE_STORAGE_KEY, next)
  }

  function handleGenerate(input: PlanGenerateInput) {
    generateMutation.mutate(input, {
      onSuccess: () => {
        setGenerateOpen(false)
        setJustGenerated(true)
      },
      onError: () => toast.error(t("segmentAnalysis.plan.generateFailed")),
    })
  }

  function openUpdate(fieldKey: string, title: string) {
    setReview({ fieldKey, title })
    requestUpdate.mutate(fieldKey, {
      onError: () => toast.error(t("segmentAnalysis.plan.updateFailed")),
    })
  }

  function acceptSuggestion(payload: FieldUpdatePayload) {
    if (!review) return
    patchField.mutate(
      { fieldKey: review.fieldKey, payload },
      {
        // Accepting a field is the "review" this banner asked for — once the
        // user has acted on at least one field, the fresh-draft framing is
        // stale even though nothing in the plan data marks fields as reviewed.
        onSuccess: () => {
          setReview(null)
          setJustGenerated(false)
        },
        onError: () => toast.error(t("segmentAnalysis.plan.saveFailed")),
      }
    )
  }

  const backLabel = t("segmentAnalysis.plan.backToCustomers", { segment: segmentName })
  const labelledFields = useMemo(() => (plan ? ccpLabelledFields(plan) : []), [plan])
  const gaps = useMemo(() => dataGapLabels(labelledFields), [labelledFields])
  const mostlyNs = generated && planIsMostlyNs(labelledFields)
  const freshDraft = justGenerated || Boolean(plan?.meta.isFreshDraft)

  const suggestion =
    requestUpdate.data && review && requestUpdate.data.fieldKey === review.fieldKey
      ? requestUpdate.data
      : null

  const shellTitle = plan?.companyName ?? companyName

  const metaSlot = plan ? (
    <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
      <span>
        <span className="font-semibold">{t("segmentAnalysis.plan.createdBy")}:</span>{" "}
        {plan.meta.createdBy ?? "—"}
      </span>
      <span>
        <span className="font-semibold">{t("segmentAnalysis.plan.date")}:</span>{" "}
        {formatPlanDate(plan.meta.createdAt)}
      </span>
      <span>
        <span className="font-semibold">{t("segmentAnalysis.plan.version")}:</span>{" "}
        {plan.meta.version ?? 1}
      </span>
      {plan.meta.area ? (
        <span>
          <span className="font-semibold">{t("segmentAnalysis.plan.area")}:</span> {plan.meta.area}
        </span>
      ) : null}
      {plan.meta.confidence ? (
        <span className="inline-flex items-center gap-1">
          <span className="font-semibold">{t("segmentAnalysis.plan.confidence")}:</span>
          <ConfidenceBadge confidence={plan.meta.confidence} />
        </span>
      ) : null}
    </div>
  ) : null

  function renderBody(fieldKey: string, onCitationClick?: (citation: FieldCitation) => void) {
    if (!plan) return null
    return (
      <CcpSectionBody
        plan={plan}
        sectionKey={fieldKey as CcpSectionKey}
        sources={plan.sources ?? []}
        onCitationClick={onCitationClick}
      />
    )
  }

  function renderPlanContent() {
    if (!generated) {
      return (
        <PlanEmptyState
          canEdit={canEdit}
          isGenerating={generateMutation.isPending}
          kind="ccp"
          onGenerate={() => setGenerateOpen(true)}
        />
      )
    }
    const planView =
      mode === "document" ? (
        <PlanDocumentView
          canEdit={canEdit}
          dataGaps={gaps}
          freshDraft={freshDraft}
          planKind="ccp"
          renderBody={renderBody}
          sections={SECTION_VIEWS}
          sources={plan?.sources ?? []}
          updateTestId={updateTestId}
          onUpdate={openUpdate}
        />
      ) : (
        <PlanCanvasView
          canEdit={canEdit}
          dataGaps={gaps}
          freshDraft={freshDraft}
          renderBody={(fieldKey) => renderBody(fieldKey)}
          sections={SECTION_VIEWS}
          updateTestId={updateTestId}
          onUpdate={openUpdate}
        />
      )
    return (
      <div className="space-y-4">
        {mostlyNs ? (
          <ThinEvidenceBanner
            canEdit={canEdit}
            isGenerating={generateMutation.isPending}
            onRegenerate={() => setGenerateOpen(true)}
          />
        ) : null}
        {planView}
      </div>
    )
  }

  if (planQuery.isPending) {
    return (
      <div className="space-y-4" data-testid="sa-ccp-plan">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  return (
    <div data-testid="sa-ccp-plan">
      <PlanShell
        backLabel={backLabel}
        canEdit={canEdit}
        isGenerating={generateMutation.isPending}
        kindLabel={t("segmentAnalysis.plan.ccpKind")}
        metaSlot={generated ? metaSlot : undefined}
        mode={mode}
        title={shellTitle}
        onBack={onBack}
        onModeChange={changeMode}
        onRegenerate={generated ? () => setGenerateOpen(true) : undefined}
      >
        <div className="space-y-4">
          <RelatedCompaniesRow
            companyName={shellTitle}
            directory={directory}
            preferSegmentId={segmentId}
            onOpenCompany={onOpenCompany}
          />
          {renderPlanContent()}
        </div>
      </PlanShell>

      <GenerateModal
        companyName={shellTitle}
        isGenerating={generateMutation.isPending}
        kind="ccp"
        libraryDocs={libraryDocs}
        open={generateOpen}
        onGenerate={handleGenerate}
        onOpenChange={setGenerateOpen}
      />
      <UpdateReviewModal
        current={suggestion?.current ?? null}
        isLoading={requestUpdate.isPending}
        isSaving={patchField.isPending}
        open={Boolean(review)}
        suggestion={suggestion?.suggestion ?? null}
        title={review?.title ?? ""}
        renderSection={(section) =>
          plan && review ? (
            <CcpSectionBody
              plan={{ ...plan, sections: { ...plan.sections, [review.fieldKey]: section } }}
              sectionKey={review.fieldKey as CcpSectionKey}
              sources={plan.sources ?? []}
            />
          ) : null
        }
        onAccept={acceptSuggestion}
        onOpenChange={(open) => !open && setReview(null)}
      />
    </div>
  )
}
